/**
 * InstructionsPage — the lab-Admin instructions.md editor (AAS-5). Reads the active instructions.md
 * (GET /assistant/instructions, readable offline) and lets a lab-Admin publish a new version
 * (PUT /assistant/instructions — append-only, version+1). The content is prepended to every assistant
 * answer's system prompt, so this is the lab's operational governance surface.
 *
 * Reading is gated on ai.assistant.query (any staff may consult the SOP); PUBLISHING is gated on
 * ai.assistant.instructions.manage (lab-Admin only) — non-admins see a read-only editor. A two-tab
 * Éditeur / Aperçu split shows the Markdown source and a dependency-free live preview.
 *
 * 5-state DoD: sans-droit (ForbiddenState if even read is missing), hors-ligne (OfflineState, but the
 * last-read content stays available), chargement (LoadingState), erreur (ErrorState with retry; a 404 =
 * the clean ModuleUnavailable state), vide (no version yet → an empty hint + the editor primed).
 *
 * Keyboard-first: Ctrl/Cmd+Enter publishes from anywhere in the editor; tabs are roving-keyboard
 * (the Tabs primitive). FR copy + AR-RTL aware; the version chip is an LTR mono island.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  ErrorState,
  ForbiddenState,
  LoadingState,
  OfflineState,
  Tabs,
  Textarea,
  useToast,
} from "@/ui";
import { useCan } from "@/auth";
import { useT, useUiStore } from "@/app";
import { AI_CAPS } from "../caps";
import { useInstructions, usePublishInstructions } from "../api";
import { isNotMounted } from "../lib";
import { PageHeader } from "../components/PageHeader";
import { MarkdownView } from "../components/MarkdownView";
import { ModuleUnavailable } from "../components/ModuleUnavailable";

export function InstructionsPage() {
  const { t } = useT();
  const toast = useToast();
  const online = useUiStore((s) => s.online);
  const canRead = useCan(AI_CAPS.query);
  const canManage = useCan(AI_CAPS.instructionsManage);

  const query = useInstructions(canRead && online);
  const publish = usePublishInstructions();

  const serverContent = query.data?.contenu_md ?? "";
  const version = query.data?.version ?? null;

  const [draft, setDraft] = useState("");
  const [tab, setTab] = useState("editor");
  const [touched, setTouched] = useState(false);

  // Hydrate the editor from the server's active version once it loads (until the user edits).
  useEffect(() => {
    if (!touched) setDraft(serverContent);
  }, [serverContent, touched]);

  const dirty = draft !== serverContent;
  const trimmed = draft.trim();

  const doPublish = () => {
    if (!canManage) return;
    if (!trimmed) {
      toast.error(t("ai.instructions.required"));
      return;
    }
    if (!dirty) {
      toast.info(t("ai.instructions.unchanged"));
      return;
    }
    publish.mutate(
      { contenu_md: draft },
      {
        onSuccess: (res) => {
          setTouched(false);
          toast.success(t("ai.instructions.published", { n: res.version }));
        },
        onError: (err) => toast.error(err.message || t("ai.instructions.publishError")),
      },
    );
  };

  const onEditorKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      doPublish();
    }
  };

  const tabs = useMemo(
    () => [
      {
        id: "editor",
        label: t("ai.instructions.editor"),
        content: (
          <Textarea
            value={draft}
            rows={16}
            onChange={(e) => {
              setTouched(true);
              setDraft(e.target.value);
            }}
            onKeyDown={onEditorKeyDown}
            placeholder={t("ai.instructions.placeholder")}
            aria-label={t("ai.instructions.field")}
            readOnly={!canManage}
            style={{ fontFamily: "var(--nv-font-mono)", minHeight: "320px" }}
          />
        ),
      },
      {
        id: "preview",
        label: t("ai.instructions.preview"),
        content: (
          <div
            style={{
              minHeight: "320px",
              padding: "var(--nv-s-3)",
              borderRadius: "var(--nv-r-sm)",
              background: "var(--nv-surface-2)",
              border: "1px solid var(--nv-line-faint)",
            }}
          >
            <MarkdownView source={draft} emptyText={t("ai.instructions.previewEmpty")} />
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [draft, canManage, t],
  );

  const header = (
    <PageHeader
      title={t("ai.instructions.title")}
      subtitle={t("ai.instructions.subtitle")}
      badge={
        <Badge tone={version ? "info" : "neutral"} glyph={version ? "▣" : "○"}>
          <span className="nv-ltr-island">
            {version ? t("ai.instructions.version", { n: version }) : t("ai.instructions.versionNone")}
          </span>
        </Badge>
      }
      actions={
        <>
          {canManage && dirty && (
            <Button
              variant="ghost"
              onClick={() => {
                setTouched(false);
                setDraft(serverContent);
              }}
              disabled={publish.isPending}
            >
              {t("ai.instructions.reset")}
            </Button>
          )}
          <Button onClick={doPublish} disabled={!canManage || publish.isPending || !dirty || !trimmed}>
            {publish.isPending ? t("ai.instructions.publishing") : t("ai.instructions.publish")}
          </Button>
        </>
      }
    />
  );

  // ── 5-state DoD, in priority order ──────────────────────────────────────────────
  if (!canRead) return <ForbiddenState requiredCapabilities={[AI_CAPS.query]} />;

  if (!online) {
    return (
      <Screen>
        {header}
        <OfflineState onRetry={() => void query.refetch()} cached={!!serverContent} />
      </Screen>
    );
  }

  if (query.isError && isNotMounted(query.error)) {
    return (
      <Screen>
        {header}
        <ModuleUnavailable />
      </Screen>
    );
  }

  if (query.isLoading) {
    return (
      <Screen>
        {header}
        <LoadingState />
      </Screen>
    );
  }

  if (query.isError) {
    return (
      <Screen>
        {header}
        <ErrorState
          message={query.error.message}
          code={query.error.code}
          onRetry={() => void query.refetch()}
        />
      </Screen>
    );
  }

  return (
    <Screen>
      {header}

      {!canManage && (
        <div
          role="note"
          style={{
            padding: "var(--nv-s-2) var(--nv-s-3)",
            borderRadius: "var(--nv-r-sm)",
            background: "var(--nv-surface-2)",
            border: "1px solid var(--nv-line-faint)",
            fontSize: "var(--nv-fs-caption)",
            color: "var(--nv-slate)",
          }}
        >
          {t("ai.instructions.readonly")}
        </div>
      )}

      {!serverContent && (
        <Card>
          <div style={{ color: "var(--nv-slate)" }}>
            <strong>{t("ai.instructions.empty.title")}</strong>
            <p style={{ margin: "var(--nv-s-1) 0 0" }}>{t("ai.instructions.empty.desc")}</p>
          </div>
        </Card>
      )}

      <Card padded={false}>
        <div style={{ padding: "var(--nv-s-3)", display: "flex", flexDirection: "column", gap: "var(--nv-s-3)" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "var(--nv-s-2)",
              flexWrap: "wrap",
            }}
          >
            <span style={{ fontSize: "var(--nv-fs-label)", color: "var(--nv-slate)" }}>
              {t("ai.instructions.field")}
            </span>
            {dirty && (
              <Badge tone="warn" glyph="▲">
                {t("ai.instructions.dirty")}
              </Badge>
            )}
          </div>

          <Tabs items={tabs} value={tab} onChange={setTab} aria-label={t("ai.instructions.title")} />
        </div>
      </Card>
    </Screen>
  );
}

/** The shared vertical-stack frame each screen state shares. */
function Screen({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>{children}</div>
  );
}
